<template>
    <HeaderCardsLayout variant="No-Logo">
        <template #headline>{{ $t('external-events') }}</template>
        <template #icon>
            <AirplaneIcon class="w-12 h-12" />
        </template>
        <template #buttons>
            <LinkButton
                variant="Info-Strong"
                size="Base"
                :disabled-explanation="createExternalEventSeriesDisabledExplanation"
                :to="{ name: 'external-event-series-add' }"
            >
                <template #icon>
                    <PlusIcon class="size-5" />
                </template>
                {{ $t('external-event-series-add') }}
            </LinkButton>
        </template>
        <template #body>
            <PaddedContent>
                <div v-if="loading" class="space-y-4">
                    <Message status="info">{{ $t('loading-external-event-series') }}</Message>
                </div>
                <div v-if="!loading && externalEventSeries.length === 0" class="space-y-4">
                    <EmptyState>
                        <template #description>
                            {{ $t('no-external-event-series') }}
                        </template>
                        <template #call-to-action>
                            <LinkButton
                                size="Base"
                                variant="Info-Strong"
                                :disabled-explanation="createExternalEventSeriesDisabledExplanation"
                                :to="{ name: 'external-event-series-add' }"
                            >
                                <template #icon>
                                    <PlusIcon class="size-5" />
                                </template>
                                {{ $t('external-event-series-add') }}
                            </LinkButton>
                        </template>
                    </EmptyState>
                </div>
                <div v-if="!loading && externalEventSeries.length > 0" class="space-y-4">
                    <CollapsibleHeadline
                        v-for="[category, externalSeries] of groupCategories"
                        :key="category"
                        :heading-level="2"
                        :initial-is-open="true"
                    >
                        <template #headline>{{ category }} ({{ externalSeries.length }})</template>
                        <template #content>
                            <div class="space-y-2">
                                <ExternalEventSeriesCard
                                    v-for="series in externalSeries"
                                    :key="series.id"
                                    :series="series"
                                    :link-text="series.nameShort"
                                    :link-target="{ name: 'external-event-series', params: { seriesId: series.id } }"
                                >
                                </ExternalEventSeriesCard>
                            </div>
                        </template>
                    </CollapsibleHeadline>
                </div>
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>
<script setup lang="ts">
import auth from '@/auth/auth';
import LinkButton from '@/components/Button/LinkButton.vue';
import CollapsibleHeadline from '@/components/CollapsibleHeadline/CollapsibleHeadline.vue';
import EmptyState from '@/components/EmptyState/EmptyState.vue';
import AirplaneIcon from '@/components/Icon/AirplaneIcon.vue';
import PlusIcon from '@/components/Icon/PlusIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import Message from '@/components/Message/Message.vue';
import { ExternalEventSeriesDataFragment, GetExternalEventSeriesDocument } from '@/generated/graphql';
import { useQuery } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { computed } from 'vue';
import ExternalEventSeriesCard from '../components/ExternalEventSeriesCard.vue';
import { EVENT_CATEGORIES } from '../externalEvent';

const fluent = useFluent();

const createExternalEventSeriesDisabledExplanation = computed(() =>
    auth.isAdmin() ? undefined : fluent.$t('no-permission-to-create'),
);

const { result: externalEventSeriesResult, loading } = useQuery(GetExternalEventSeriesDocument);
const externalEventSeries = computed(() => externalEventSeriesResult.value?.externalEventSerieses?.nodes ?? []);

const groupCategories = computed(() => {
    const stateMap: Map<string, Array<ExternalEventSeriesDataFragment>> = new Map();
    stateMap.set(
        'Feiertage (national)',
        externalEventSeries.value.filter((s) => s.national && s.category === 'public_holiday') ?? [],
    );
    stateMap.set(
        'Ereignisse (national)',
        externalEventSeries.value.filter((s) => s.national && EVENT_CATEGORIES.includes(s.category)) ?? [],
    );
    stateMap.set(
        'Ferien (national)',
        externalEventSeries.value.filter((s) => s.national && s.category === 'holiday') ?? [],
    );
    stateMap.set(
        'Wettbewerbsveranstaltungen (national)',
        externalEventSeries.value.filter((s) => (s.national && s.category === 'competition') ?? []),
    );
    stateMap.set(
        'Koelnmesse weltweit (national)',
        externalEventSeries.value.filter((s) => (s.national && s.category === 'world') ?? []),
    );
    stateMap.set(
        'Feiertage (international)',
        externalEventSeries.value.filter((s) => !s.national && s.category === 'public_holiday') ?? [],
    );
    stateMap.set(
        'Ereignisse (international)',
        externalEventSeries.value.filter((s) => !s.national && EVENT_CATEGORIES.includes(s.category)) ?? [],
    );
    stateMap.set(
        'Ferien (international)',
        externalEventSeries.value.filter((s) => !s.national && s.category === 'holiday') ?? [],
    );
    stateMap.set(
        'Wettbewerbsveranstaltungen (international)',
        externalEventSeries.value.filter((s) => (!s.national && s.category === 'competition') ?? []),
    );
    stateMap.set(
        'Koelnmesse weltweit (international)',
        externalEventSeries.value.filter((s) => (!s.national && s.category === 'world') ?? []),
    );
    return stateMap;
});
</script>
